import { inspect } from 'util';
import { Breadcrumb } from '@sentry/react';
import { Extras, TransactionContext } from '@sentry/types';
import { GraphQLError } from 'graphql';

const isGraphApi = (breadcrumb: Breadcrumb) =>
  breadcrumb.category === 'fetch' && breadcrumb.data?.url.endsWith('/graphql')
    ? true
    : false;

const isConsole = (breadcrumb: Breadcrumb, isProduction: boolean) =>
  isProduction && breadcrumb.category === 'console';

const hasExtension = (error: Error): boolean => {
  const graphError = error as GraphQLError;
  return graphError.extensions ? true : false;
};

const scrubError = (err: GraphQLError) => {
  delete err?.extensions?.variables;
};

export const tracingOrigins = (): (string | RegExp)[] => {
  const origins = ['graph.ti.replicon.com', /^\//];
  return process.env.NODE_ENV === 'production'
    ? origins
    : ['127.0.0.1', ...origins];
};

export const shouldIgnoreBreakCrumbs = (
  breadcrumb: Breadcrumb,
  isProduction: boolean
): boolean => isConsole(breadcrumb, isProduction) || isGraphApi(breadcrumb);

export const isSentryEnabled = (): boolean =>
  process.env.NODE_ENV === 'production' ||
  process.env.REACT_APP_ENABLE_SENTRY === 'true';

export const samplingContext = (): number => {
  return process.env.NODE_ENV === 'production' ? 0.1 : 1;
};

export const extractError = (err: Error): Error => {
  if (hasExtension(err)) {
    const retError = new Error(err.message);
    retError.name = err.name;
    retError.stack = err.stack;
    return retError;
  }
  return err;
};

export const getExtraFromExtension = (err: Error): Extras | undefined => {
  if (!hasExtension(err)) return undefined;
  const graphError = err as GraphQLError;
  const {
    path = [],
    extensions: {
      code = '',
      serviceName = '',
      exception: { stacktrace = [] } = {}
    } = {}
  } = graphError;
  scrubError(graphError);
  return {
    code: code,
    serviceName: serviceName,
    path: path?.join(' > '),
    stacktrace: stacktrace?.join('\n'),
    extensions: JSON.stringify(inspect(graphError?.extensions))
  };
};

export const modifyTransactionNameIfRequired = (
  context: TransactionContext,
  currentUrl: string
): TransactionContext => {
  const trnName = context.name.trim();
  const newName =
    trnName.endsWith('/') && trnName.length > 1
      ? `${trnName}${currentUrl.substring(
          currentUrl.indexOf(trnName) + trnName.length
        )}`
      : trnName;
  return {
    ...context,
    name: newName
  };
};
